import { useEffect, useRef, useState, useCallback } from "react";
import { store } from "dataStore";

import { normalizeEvent } from "pages/LiveEventTracking/utils";

const QA_WS_HOST = "qa.msme.jswone.in";
const PROD_WS_HOST = "www.jswonemsme.com";
const NOTIFICATION_WS_PATH = "/notification-api/ws";
const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const MAX_RECONNECT_ATTEMPTS = 4;
const CACHE_KEY = "liveEventTracking:lastEvents";
const CACHE_SIZE = 50;

// Same access token axios sends as "Authorization: Bearer ...". Read on every
// connect so a refreshed token is picked up on reconnect.
const getAccessToken = () => {
  const { isLoggedIn } = store.getState();
  return isLoggedIn?.value ? isLoggedIn.token : "";
};

// Browsers can't set headers on a WebSocket, so the server reads the token from
// the query string. Never log this URL (or the socket / its events, which carry it in `.url`).
const getNotificationWsUrl = (token) => {
  const host = process.env.REACT_APP_ENV === "PROD" ? PROD_WS_HOST : QA_WS_HOST;
  return `wss://${host}${NOTIFICATION_WS_PATH}?token=${encodeURIComponent(
    token,
  )}`;
};

// 2s, 4s, 8s, 16s, 30s (capped)
const getReconnectDelay = (attempt) =>
  Math.min(BASE_RECONNECT_DELAY_MS * 2 ** attempt, MAX_RECONNECT_DELAY_MS);

// Keep last few events in localStorage so the list isn't empty on reload.
const readCachedEvents = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .slice(0, CACHE_SIZE)
      .map(normalizeEvent)
      .filter(Boolean)
      .map((event) => ({ ...event, fromCache: true }));
  } catch {
    return [];
  }
};

const writeCachedEvents = (events) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify(events.slice(0, CACHE_SIZE)),
    );
  } catch {
    // Ignore storage errors; cache is optional.
  }
};

// When `enabled` is false, no API call or socket connection is made.
export const useLiveEventSocket = (maxEvents = 200, enabled = true) => {
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  // Start with cached events; new ones are added to this list.
  const [events, setEvents] = useState(readCachedEvents);
  const [connectionError, setConnectionError] = useState("");
  // Set only for new socket events, not for events loaded from the cache.
  const [latestLiveEvent, setLatestLiveEvent] = useState(null);

  const clearEvents = useCallback(() => {
    setEvents([]);
    writeCachedEvents([]);
  }, []);

  // Save current events to cache (including when cleared).
  useEffect(() => {
    writeCachedEvents(events.map(({ fromCache, ...rest }) => rest));
  }, [events]);

  // Connect to the notification socket; it sends every event to all clients.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let attempts = 0;

    const scheduleReconnect = () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > MAX_RECONNECT_ATTEMPTS) {
        // Most likely the token expired or was rejected.
        setConnectionError(
          "Live events disconnected. Please refresh the page or log in again.",
        );
        return;
      }
      reconnectTimerRef.current = setTimeout(
        connect,
        getReconnectDelay(attempts),
      );
    };

    const connect = () => {
      const token = getAccessToken();
      if (!token) {
        setConnectionError("Log in to receive live events");
        return;
      }

      let socket;
      try {
        socket = new WebSocket(getNotificationWsUrl(token));
      } catch {
        // Don't log the error: its message contains the URL (and token).
        console.error("Live event socket could not be created");
        setConnectionError("Unable to connect to live events");
        scheduleReconnect();
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        attempts = 0;
        setIsConnected(true);
        setConnectionError("");
      };

      socket.onmessage = (message) => {
        let payload;
        try {
          payload = JSON.parse(message.data);
        } catch {
          payload = { eventPayload: message.data };
        }

        // Skip messages that are not a single valid event.
        if (
          !payload ||
          typeof payload !== "object" ||
          Array.isArray(payload) ||
          !payload.eventId
        ) {
          console.warn(
            "[LiveEvents] Ignoring malformed/unexpected live message:",
            payload,
          );
          return;
        }

        const newEvent = normalizeEvent({ ...payload, receivedAt: Date.now() });
        // Always add new event on top, even if eventId repeats.
        setEvents((prev) => [newEvent, ...prev].slice(0, maxEvents));
        setLatestLiveEvent(newEvent);
      };

      socket.onerror = () => {
        // No reason given here; check onclose for the actual cause.
        // Don't log the event object: event.target.url contains the token.
        console.error("Live event socket connection error");
      };

      socket.onclose = (event) => {
        console.error(
          `Live event socket closed: code=${event.code} reason="${event.reason}" wasClean=${event.wasClean}`,
        );
        setConnectionError(
          `WebSocket closed (code ${event.code}${
            event.reason ? `: ${event.reason}` : ""
          })`,
        );
        setIsConnected(false);
        socketRef.current = null;
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [maxEvents, enabled]);

  return { isConnected, events, connectionError, clearEvents, latestLiveEvent };
};

export default useLiveEventSocket;
